/**
 * Racing Pattern Generation Utility for Enhanced Race Animation
 *
 * This utility generates dynamic racing patterns that create suspenseful and varied
 * movement for competitors while ensuring the correct winner can achieve victory
 * through different dramatic scenarios.
 */

// Interface for speed segments within a racing pattern
export interface SpeedSegment {
  startTime: number;
  endTime: number;
  speedMultiplier: number;
  easing: "linear" | "ease-in" | "ease-out" | "ease-in-out";
}

// Interface for a complete racing pattern for one competitor
export interface RacingPattern {
  competitorId: string;
  speedVariations: SpeedSegment[];
  comebackTiming?: number;
  finalBurst?: boolean;
}

// Configuration for pattern generation
export interface PatternGenerationConfig {
  totalRaceTime: number;
  minSpeedVariation: number;
  maxSpeedVariation: number;
  comebackProbability: number;
  finalBurstProbability: number;
  segmentCount?: number;
}

// Default configuration values
const DEFAULT_CONFIG: PatternGenerationConfig = {
  totalRaceTime: 8000, // 8 seconds total race time
  minSpeedVariation: 0.6, // Minimum speed multiplier (60% of base speed)
  maxSpeedVariation: 1.8, // Maximum speed multiplier (180% of base speed)
  comebackProbability: 0.3, // 30% chance for comeback mechanics
  finalBurstProbability: 0.4, // 40% chance for final burst
  segmentCount: 4, // Number of speed segments per competitor
};

/**
 * Generates a random speed multiplier within the configured range
 */
function generateSpeedMultiplier(
  minSpeed: number,
  maxSpeed: number,
  bias?: "slow" | "fast" | "normal"
): number {
  let multiplier = Math.random() * (maxSpeed - minSpeed) + minSpeed;

  // Apply bias if specified
  if (bias === "slow") {
    // Bias towards slower speeds (lower end of range)
    multiplier = minSpeed + (multiplier - minSpeed) * 0.7;
  } else if (bias === "fast") {
    // Bias towards faster speeds (higher end of range)
    multiplier = minSpeed + (multiplier - minSpeed) * 1.3;
    multiplier = Math.min(multiplier, maxSpeed); // Ensure we don't exceed max
  }

  return Math.round(multiplier * 100) / 100; // Round to 2 decimal places
}

/**
 * Generates time segments for a racing pattern
 */
function generateTimeSegments(
  totalTime: number,
  segmentCount: number
): Array<{ startTime: number; endTime: number }> {
  const segments: Array<{ startTime: number; endTime: number }> = [];
  const segmentDuration = totalTime / segmentCount;

  for (let i = 0; i < segmentCount; i++) {
    const startTime = i * segmentDuration;
    const endTime = (i + 1) * segmentDuration;

    segments.push({
      startTime: Math.round(startTime),
      endTime: Math.round(endTime),
    });
  }

  return segments;
}

/**
 * Selects appropriate easing function based on speed transition
 */
function selectEasing(
  currentSpeed: number,
  nextSpeed: number
): SpeedSegment["easing"] {
  const speedDiff = nextSpeed - currentSpeed;

  if (Math.abs(speedDiff) < 0.2) {
    return "linear"; // Small changes use linear
  } else if (speedDiff > 0) {
    return "ease-out"; // Accelerating - start fast, ease into new speed
  } else {
    return "ease-in"; // Decelerating - ease into slowdown
  }
}

/**
 * Generates a racing pattern for a single competitor
 */
export function generateCompetitorPattern(
  competitorId: string,
  isWinner: boolean,
  config: Partial<PatternGenerationConfig> = {}
): RacingPattern {
  const fullConfig = { ...DEFAULT_CONFIG, ...config };
  const {
    totalRaceTime,
    minSpeedVariation,
    maxSpeedVariation,
    comebackProbability,
    finalBurstProbability,
    segmentCount = 4,
  } = fullConfig;

  // Generate time segments
  const timeSegments = generateTimeSegments(totalRaceTime, segmentCount);
  const speedVariations: SpeedSegment[] = [];

  // Determine pattern type for this competitor
  const hasComeback = Math.random() < comebackProbability;
  const hasFinalBurst = Math.random() < finalBurstProbability;

  // Generate speed multipliers for each segment
  const speedMultipliers: number[] = [];

  for (let i = 0; i < segmentCount; i++) {
    let bias: "slow" | "fast" | "normal" = "normal";

    // Apply pattern-specific biases
    if (hasComeback && !isWinner) {
      // Non-winners with comeback: start fast, slow down, then recover
      if (i === 0) bias = "fast";
      else if (i === 1 || i === 2) bias = "slow";
      else bias = "fast";
    } else if (hasComeback && isWinner) {
      // Winners with comeback: start slow, then accelerate
      if (i < segmentCount - 2) bias = "slow";
      else bias = "fast";
    } else if (hasFinalBurst) {
      // Final burst pattern: moderate speeds with fast finish
      if (i === segmentCount - 1) bias = "fast";
      else bias = "normal";
    }

    const multiplier = generateSpeedMultiplier(
      minSpeedVariation,
      maxSpeedVariation,
      bias
    );
    speedMultipliers.push(multiplier);
  }

  // Ensure winner has advantage in final segments if they're behind
  if (isWinner && hasComeback) {
    // Boost final segments for winner comeback
    const finalSegmentIndex = segmentCount - 1;
    speedMultipliers[finalSegmentIndex] = Math.max(
      speedMultipliers[finalSegmentIndex],
      maxSpeedVariation * 0.9
    );
  }

  // Create speed segments with appropriate easing
  for (let i = 0; i < timeSegments.length; i++) {
    const timeSegment = timeSegments[i];
    const currentSpeed = speedMultipliers[i];
    const nextSpeed =
      i < speedMultipliers.length - 1 ? speedMultipliers[i + 1] : currentSpeed;

    speedVariations.push({
      startTime: timeSegment.startTime,
      endTime: timeSegment.endTime,
      speedMultiplier: currentSpeed,
      easing: selectEasing(currentSpeed, nextSpeed),
    });
  }

  return {
    competitorId,
    speedVariations,
    comebackTiming: hasComeback ? totalRaceTime * 0.6 : undefined, // Comeback starts at 60% of race
    finalBurst: hasFinalBurst,
  };
}

/**
 * Determines if suspense patterns should be applied based on competitor closeness
 */
export function shouldApplySuspensePatterns(
  competitors: Array<{ id: string; points: number; bugs: number }>,
  winnerId?: string
): boolean {
  if (!winnerId || competitors.length < 2) {
    return false;
  }

  const winner = competitors.find((c) => c.id === winnerId);
  if (!winner) {
    return false;
  }

  // Calculate winner's total score (points - bugs)
  const winnerScore = winner.points - winner.bugs;

  // Find the closest competitor to the winner
  let closestScore = -Infinity;
  for (const competitor of competitors) {
    if (competitor.id !== winnerId) {
      const competitorScore = competitor.points - competitor.bugs;
      if (competitorScore > closestScore) {
        closestScore = competitorScore;
      }
    }
  }

  // Only apply suspense if the closest competitor is within 20% of the winner's score
  const scoreGap = winnerScore - closestScore;
  const suspenseThreshold = winnerScore * 0.2; // 20% of winner's score

  return scoreGap <= suspenseThreshold;
}

// Cache for pattern generation to avoid recalculating identical patterns
const patternCache = new Map<string, RacingPattern>();

/**
 * Generates a cache key for pattern generation
 */
function generateCacheKey(
  competitorId: string,
  isWinner: boolean,
  config: PatternGenerationConfig
): string {
  return `${competitorId}-${isWinner}-${JSON.stringify(config)}`;
}

/**
 * Generates racing patterns for all competitors with performance optimizations
 */
export function generateRacingPatterns(
  competitorIds: string[],
  winnerId?: string,
  config: Partial<PatternGenerationConfig> = {},
  competitors?: Array<{ id: string; points: number; bugs: number }>
): RacingPattern[] {
  if (competitorIds.length === 0) {
    return [];
  }

  // Determine if we should apply suspense patterns (optimized calculation)
  const applySuspense =
    competitors && competitors.length > 1
      ? shouldApplySuspensePatterns(competitors, winnerId)
      : false;

  // Pre-calculate pattern config to avoid repeated object creation
  const baseConfig = { ...DEFAULT_CONFIG, ...config };
  const simplifiedConfig = applySuspense
    ? baseConfig
    : {
        ...baseConfig,
        comebackProbability: 0.1, // Minimal comeback chance
        finalBurstProbability: 0.2, // Minimal final burst chance
        minSpeedVariation: 0.8, // Less speed variation
        maxSpeedVariation: 1.3,
      };

  const patterns: RacingPattern[] = [];

  // Use batch processing for better performance with many competitors
  for (const competitorId of competitorIds) {
    const isWinner = winnerId === competitorId;
    const patternConfig = applySuspense ? baseConfig : simplifiedConfig;

    // Check cache first for performance
    const cacheKey = generateCacheKey(competitorId, isWinner, patternConfig);
    let pattern = patternCache.get(cacheKey);

    if (!pattern) {
      pattern = generateCompetitorPattern(
        competitorId,
        isWinner,
        patternConfig
      );

      // Cache the pattern but limit cache size to prevent memory leaks
      if (patternCache.size < 100) {
        patternCache.set(cacheKey, pattern);
      }
    }

    patterns.push(pattern);
  }

  return patterns;
}

/**
 * Clears the pattern generation cache (useful for testing or memory management)
 */
export function clearPatternCache(): void {
  patternCache.clear();
}

/**
 * Gets the speed multiplier for a competitor at a specific time
 */
export function getSpeedMultiplierAtTime(
  pattern: RacingPattern,
  currentTime: number
): number {
  // Find all segments that contain the current time
  const matchingSegments = pattern.speedVariations.filter(
    (segment) =>
      currentTime >= segment.startTime && currentTime <= segment.endTime
  );

  if (matchingSegments.length === 0) {
    // If no segment found, return base speed
    return 1.0;
  }

  // If multiple segments match (at boundary), prefer the one that starts at currentTime
  const segmentStartingAtTime = matchingSegments.find(
    (segment) => segment.startTime === currentTime
  );

  const currentSegment = segmentStartingAtTime || matchingSegments[0];
  return currentSegment.speedMultiplier;
}

/**
 * Validates that racing patterns are consistent and complete
 */
export function validateRacingPatterns(
  patterns: RacingPattern[],
  competitorIds: string[]
): boolean {
  // Check that we have patterns for all competitors
  if (patterns.length !== competitorIds.length) {
    return false;
  }

  // Check that all competitor IDs are present
  const patternIds = new Set(patterns.map((p) => p.competitorId));
  const expectedIds = new Set(competitorIds);

  if (patternIds.size !== expectedIds.size) {
    return false;
  }

  for (const id of expectedIds) {
    if (!patternIds.has(id)) {
      return false;
    }
  }

  // Validate each pattern has valid speed segments
  for (const pattern of patterns) {
    if (!pattern.speedVariations || pattern.speedVariations.length === 0) {
      return false;
    }

    // Check that segments are in chronological order
    for (let i = 1; i < pattern.speedVariations.length; i++) {
      const prev = pattern.speedVariations[i - 1];
      const current = pattern.speedVariations[i];

      if (current.startTime < prev.endTime) {
        return false;
      }
    }
  }

  return true;
}
